import { useState, useEffect } from "react";
import { TreeSelect } from "antd";
import LoadingButton from "@mui/lab/LoadingButton";
import GenericAlert from "../../generic/genericAlert";
import Stack from "@mui/material/Stack";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";

/**
 * Select dropdown for different tag types
 * 
 * @param {*} props 
 * @prop {number} groupType must be a valid HierarchyGroupType value, defaults to 1
 * @prop {boolean} loadInComponent if True, run backend requests for tree data on load
 * @prop {boolean} lazyLoad if True and loadInComponent is True, load data on first dropdown open instead of mount
 * @prop {string} placeholder placeholder text
 * @prop {callable} handleApply optional callback, renders an Apply button if it exists
 * @prop {boolean} multiSelect if True, allow multiple selection in tree
 * @prop {string} size optional size setting, defaults to small
 * @prop {boolean} loading optional loading prop if loading externally
 * @prop {callable} onChange optional callable executed on select, passes in array of tag/group names with ids. Ex: ['tag_108', 'group_36'] 
 * @prop {array.number} currentTagIds array of generic tag objects to prefill select
 */
const SelectBankAccountTag = (props) => {
  const [tagMap, setTagMap] = useState({});
  const [groupMap, setGroupMap] = useState({});
  const [alertMessage, setAlertMessage] = useState(undefined);
  const [selectedValues, setSelectedValues] = useState(undefined);
  const [customTags, setCustomTags] = useState([]);
  const [groups, setGroups] = useState([]);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const loadGroupData = () => {
    return backendGetRequest("/api/generic-tag/group/", { params: { page_index: 0, group_type: props.groupType || 1 } })
      .then((res) => {
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequestWithCache("api/generic-tag/group/", { params: { page_index: i, group_type: props.groupType || 1 } },
            (cachedResponse) => {
              const cachedGroups = cachedResponse?.data?.generic_groups || [];
              if (!cachedGroups) return;
              setGroups(prevState => {
                const updatedGroups = prevState ? [...prevState] : [];
                cachedGroups.forEach(group => {
                  if (!updatedGroups.some(existingGroup => existingGroup.id === group.id)) {
                    updatedGroups.push(group);
                  }
                });
                return updatedGroups;
              });
            },
            (nonCachedResponse) => {
              const nonCachedGroups = nonCachedResponse?.data?.generic_groups || [];
              if (!nonCachedGroups) return;
              setGroups(prevState => {
                const updatedGroups = prevState ? [...prevState] : [];
                nonCachedGroups.forEach(group => {
                  const index = updatedGroups.findIndex(existingGroup => existingGroup.id === group.id);
                  if (index !== -1) {
                    updatedGroups[index] = group;
                  } else {
                    updatedGroups.push(group);
                  }
                });
                return updatedGroups;
              });
            },
            (err) => {
              console.error('Error loading group data:', err);
              throw err;
            }
          );
        }
      })
      .catch((err) => {
        console.error('Error loading group data:', err);
        throw err;
      });
  };

  const loadTagData = () => {
    return backendGetRequest("/api/generic-tag/", { params: { page_index: 0, group_type: props.groupType || 1 } })
      .then((res) => {
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequestWithCache("api/generic-tag/", { params: { page_index: i, group_type: props.groupType || 1 } },
            (cachedResponse) => {
              const cachedTags = cachedResponse?.data?.generic_tags || [];
              if (!cachedTags) return;
              setCustomTags(prevState => {
                const updatedTags = prevState ? [...prevState] : [];
                cachedTags.forEach(tag => {
                  if (!updatedTags.some(existingTag => existingTag.id === tag.id)) {
                    updatedTags.push(tag);
                  }
                });
                return updatedTags;
              });
            },
            (nonCachedResponse) => {
              const nonCachedTags = nonCachedResponse?.data?.generic_tags || [];
              if (!nonCachedTags) return;
              setCustomTags(prevState => {
                const updatedTags = prevState ? [...prevState] : [];
                nonCachedTags.forEach(tag => {
                  const index = updatedTags.findIndex(existingTag => existingTag.id === tag.id);
                  if (index !== -1) {
                    updatedTags[index] = tag;
                  } else {
                    updatedTags.push(tag);
                  }
                });
                return updatedTags;
              });
            },
            (err) => {
              console.error('Error loading tag data:', err);
              throw err;
            }
          );
        }
      })
      .catch((err) => {
        console.error('Error loading tag data:', err);
        throw err;
      });
  };

  const loadAllData = () => {
    if (hasLoadedData || isLoadingData) return;
    setIsLoadingData(true);
    Promise.all([loadGroupData(), loadTagData()])
      .then(() => {
        setHasLoadedData(true);
        setIsLoadingData(false);
      })
      .catch((err) => {
        console.error('Error loading data:', err);
        setIsLoadingData(false);
      });
  };

  const buildTree = () => {
    const newGroupMap = {};
    const newTagMap = {};
    const tree = [];
    // Populate groupMap with all groups from props
    groups.forEach((group) => {
      newGroupMap[group.id] = {
        value: `group_${group.id}`,
        title: group.name,
        children: [],
      };
    });

    customTags.forEach((tag) => {
      newTagMap[tag.id] = {
        value: `tag_${tag.id}`,
        title: tag.name,
        group: tag.group,
        children: [],
      };
    });
    // Assign tags to their respective groups or parents
    customTags.forEach((tag) => {
      const childNode = newTagMap[tag.id];
      if (Boolean(tag.parent) && Boolean(newTagMap[tag.parent])) {
        // If tag has a parent, add it to the parent's children
        newTagMap[tag.parent].children.push(childNode);
      } else if (Boolean(tag.group_id) && Boolean(newGroupMap[tag.group_id])) {
        // If tag belongs to a group, add it to the group's children
        newGroupMap[tag.group_id].children.push(childNode);
      }
    });

    Object.values(newGroupMap)
      .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })) // Sorting here
      .forEach((group) => {
        if (group.children.length > 0) {
          group.children.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
        }
        tree.push(group);
      });

    // Sort tag children alphabetically
    Object.values(newTagMap).forEach((tag) => {
      if (tag.children.length > 0) {
        tag.children.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
      }
    });

    if (props.includeUncategorized) {
      const uncategorizedGroup = {
        value: `group_${-1}`,
        title: "Uncategorized",
        children: [],
      };
      tree.push(uncategorizedGroup);
    }

    const stringKeyTagMap = Object.fromEntries(
      Object.entries(newTagMap).map(([key, value]) => [`tag_${key}`, value])
    );
    const stringKeyGroupMap = Object.fromEntries(
      Object.entries(newGroupMap).map(([key, value]) => [`group_${key}`, value])
    );

    setTagMap(stringKeyTagMap);
    setGroupMap(stringKeyGroupMap);

    return tree;
  };

  const onFormSubmitSingle = () => {
    let tagId = null;
    let groupId = null;
    const values = selectedValues;

    if (values !== undefined) {
      if (values.startsWith('group_')) {
        // If a group is selected, pass groupId and null for tagId
        groupId = parseInt(values.replace('group_', ''), 10);
      } else if (values.startsWith('tag_')) {
        // If a tag is selected, pass its ID and its groupId
        const tag = tagMap[values];
        if (tag) {
          tagId = parseInt(values.replace('tag_', ''), 10);
          groupId = tag.group || null;
        }
      }
    }
    if (!tagId && groupId != -1) {
      setAlertMessage("Please select a tag within a hierarchy.");
      return;
    }

    localStorage.setItem("currentTagId", JSON.stringify(tagId));
    localStorage.setItem("currentGroupId", JSON.stringify(groupId));

    props.handleApply(tagId, groupId);
  };

  const onFormSubmitMulti = () => {

    const values = selectedValues;

    const selections = Array.isArray(values) ? values : [values];
    const tagIds = [];
    const groupIds = [];
    const tagsByGroup = {};

    selections.forEach(item => {
      if (item.startsWith('group_')) {
        const groupId = parseInt(item.replace('group_', ''), 10);
        groupIds.push(groupId);
      } else if (item.startsWith('tag_')) {
        const tagId = parseInt(item.replace('tag_', ''), 10);
        const tag = tagMap[item];
        if (tag) {
          tagIds.push(tagId);
          if (tag.group) {
            groupIds.push(tag.group);

            // Track tags by group for validation
            if (!tagsByGroup[tag.group]) {
              tagsByGroup[tag.group] = [];
            }
            tagsByGroup[tag.group].push(tagId);
          }
        }
      }
    });

    // Validate that there aren't multiple tags from the same group
    if (Array.isArray(values.tag)) {
      const groupsWithMultipleTags = Object.keys(tagsByGroup).filter(
        groupId => tagsByGroup[groupId].length > 1
      );

      if (groupsWithMultipleTags.length > 0) {
        const groupNames = groupsWithMultipleTags.map(
          groupId => groupMap[`group_${groupId}`]?.title || `Group ${groupId}`
        ).join(", ");

        setAlertMessage(`Cannot select multiple tags from the same hierarchy (${groupNames})`);
        return;
      }
    }

    if (!Array.isArray(values.tag) && tagIds.length === 0 &&
      groupIds.length === 1 && groupIds[0] !== -1) {
      setAlertMessage("Please select a tag within a hierarchy.");
      return;
    }

    props.handleApply(tagIds, groupIds);
  };

  const [treeData, setTreeData] = useState([]);

  const handleMouseEnter = () => {
    if (props.loadInComponent && props.lazyLoad && !hasLoadedData && !isLoadingData) {
      loadAllData();
    }
  };

  useEffect(() => {
    setSelectedValues(props.value)
  }, [props.value])

  useEffect(() => {
    if (!props.loadInComponent) return;
    if (props.lazyLoad) return; // Don't load on mount if lazy loading is enabled
    
    loadAllData();
  }, [])

  useEffect(() => {
    setTreeData(buildTree());
  }, [customTags, groups]);

  useEffect(() => {
    if (props.customTags) setCustomTags(props.customTags)
  }, [props.customTags]);

  useEffect(() => {
    if (props.groups) setGroups(props.groups)
  }, [props.groups]);

  useEffect(() => {
    let initialTagId = undefined

    if (props.currentTagId) {
      const currentTagId = `tag_${props.currentTagId}`
      if (tagMap[currentTagId]) {
        initialTagId = currentTagId
      }
    } else if (props.currentGroupId) {
      const currentGroupId = `group_${props.currentGroupId}`
      if (groupMap[currentGroupId]) {
        initialTagId = currentGroupId
      }
    } else if (props.currentTagIds) {
      initialTagId = []
      for (let tag of props.currentTagIds) {
        let cti = `tag_${tag.id}`
        if (tagMap[cti]) {
          initialTagId.push(cti)
        }
      }
    }

    // Update the form field dynamically
    setSelectedValues(initialTagId)
  }, [treeData, tagMap, groupMap, props.currentTagId, props.currentGroupId, props.currentTagIds]);

  return (
    <>
      <GenericAlert message={alertMessage} setMessage={setAlertMessage} />
      <Stack direction="row" spacing={2}>

        <TreeSelect
          style={{ width: "100%" }}
          showArrow
          showSearch={true}
          dropdownStyle={{ maxHeight: 400, overflow: "auto", zIndex: 2000 }}
          placeholder={
            isLoadingData
              ? "Loading hierarchy data..."
              : props.placeholder || "Please select a hierarchy to see accounts"
          }
          treeData={treeData}
          value={selectedValues}
          onChange={(e) => {
            setSelectedValues(e)
            if (props.onChange) {
              props.onChange(e)
            }
            if (props.onSelectionChange) {
              props.onSelectionChange(e)
            }
          }}
          onMouseEnter={handleMouseEnter}
          disabled={props.loadInComponent && props.lazyLoad && !hasLoadedData}
          allowClear
          multiple={props.multiSelect}
          treeNodeFilterProp="title"
          size={props.size || 'medium'}
          loading={isLoadingData}
        />
        {props.showFindNewAccounts && (
          <LoadingButton
            loading={props.findingAccounts}
            sx={{
              fontSize: 14,
              textTransform: "none",
              width: "240px",
            }}
            disableElevation
            loadingPosition="start"
            fullWidth
            size="small"
            variant="outlined"
            color="secondary"
            onClick={props.onFindNewAccounts}
            disabled={props.findingAccounts}
          >
            Find New Accounts
          </LoadingButton>
        )}
        {props.handleApply !== undefined && (
          <LoadingButton
            loading={props.loading}
            sx={{
              fontSize: 14,
              textTransform: "none",
              width: "100px",
            }}
            disableElevation
            loadingPosition="start"
            fullWidth
            size="small"
            variant="contained"
            color="primary"
            onClick={props.multiSelect ? onFormSubmitMulti : onFormSubmitSingle}
            htmltype="submit"
            disabled={props.loading}
          >
            Apply
          </LoadingButton>
        )}
      </Stack>
    </>
  );
};

export default SelectBankAccountTag;
